import React from 'react';
import { AccountTier, VaultRole, VaultStatus, type VaultSummaryResponse } from '../types/api';
import './VaultCard.css';

interface Props {
  vault: VaultSummaryResponse;
  onClick: () => void;
}

const roleLabels: Record<number, string> = {
  [VaultRole.Owner]: 'Owner',
  [VaultRole.Spouse]: 'Spouse',
  [VaultRole.Witness]: 'Witness',
};

function roleDescription(role: VaultRole, ownerName: string): string {
  switch (role) {
    case VaultRole.Owner:      return 'Your vault — full access to add, edit, and manage everything';
    case VaultRole.Spouse:     return `Shared vault — you can view and edit everything with ${ownerName.split(' ')[0]}`;
    case VaultRole.Witness:    return `Viewing ${ownerName.split(' ')[0]}'s vault — you can see everything but not make changes`;
    default:                   return `Viewing ${ownerName.split(' ')[0]}'s vault — read only`;
  }
}

const tierLabels: Record<number, string> = {
  [AccountTier.Trial]: 'Free Trial',
  [AccountTier.Paid]: 'Active',
  [AccountTier.ForeverFree]: 'Forever Free',
};

const tierClasses: Record<number, string> = {
  [AccountTier.Trial]: 'vault-card-tier--trial',
  [AccountTier.Paid]: 'vault-card-tier--paid',
  [AccountTier.ForeverFree]: 'vault-card-tier--free',
};

export default function VaultCard({ vault, onClick }: Props) {
  const isActive = vault.status === VaultStatus.Active || vault.status === VaultStatus.PastDue;
  const isOwner = vault.userRole === VaultRole.Owner;

  return (
    <div className="vault-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className="vault-card-header">
        <h3 className="vault-card-name">{vault.name}</h3>
        {isOwner && vault.accountTier != null && (
          <span className={`vault-card-tier ${tierClasses[vault.accountTier] ?? ''}`}>
            {tierLabels[vault.accountTier] ?? ''}
          </span>
        )}
      </div>
      <div className="vault-card-meta">
        <span className={`vault-card-status vault-card-status--${isActive ? 'active' : 'inactive'}`}>
          {isActive ? 'Active' : vault.status === VaultStatus.ReadOnly ? 'Read Only' : 'Suspended'}
        </span>
        <span className="vault-card-role">{roleLabels[vault.userRole] || 'Member'}</span>
        <span>{vault.memberCount} member{vault.memberCount !== 1 ? 's' : ''}</span>
      </div>
      <p className="vault-card-role-desc">{roleDescription(vault.userRole, vault.ownerName)}</p>
    </div>
  );
}
